import { Injectable } from '@angular/core'
import {
  HttpService,
  IExportFileResponse,
  INew_TableAction,
  TableActionsService
} from '@lla-platform/core/core-data-access'
import { Observable } from 'rxjs'
import { ADMIN_API_URL } from './admin-api-routes'
import {
  IAdminShopTiersResponse,
  IAdminWeeklyTiersUpdate
} from '../interfaces/admin-shop-tires.interface'
import { Permission } from '@lla-platform/users/users-data-access'
import {
  IAdminUpdatePermissionsRequest,
  IAdminUserPermissionsResponse,
  IBulkEditLocationsRequest,
  IBulkEditPermissionsRequest
} from '../interfaces/admin-user-permissions.interface'
import { IAdminUsersListResponse } from '../interfaces/admin-users-list.interface'
import {
  IAdminLocationInfo,
  IAdminLocationsListResponse,
  IAdminUpdateLocationRequest,
  IAdminUsafAccountStatus
} from '../interfaces/admin-locations-list.interface'
import { IAdminProtractorLocationsResponse } from '../interfaces/admin-protractor-locations.interface'
import { IAdminCreateLocationRequest } from '../interfaces/admin-location-create.interface'
import { IAdminDailyReports } from '../interfaces/admin-daily-reports.interface'
import { IPostQueueMessageRequest } from '../interfaces/admin-queue-messages.interface'
import {
  IApplyAttentionProposalsRequest,
  IAttentionProposalsResponse
} from '../interfaces/admin-attention-proposals.interface'
import { DateService } from '@lla-platform/core/core-util'

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  constructor(
    private http: HttpService,
    private tableActionsService: TableActionsService,
    private dateService: DateService
  ) {}

  getWeeklyTires(): Observable<IAdminShopTiersResponse> {
    return this.http.get(ADMIN_API_URL.weeklyTargets)
  }

  updateWeeklyTires(payload: IAdminWeeklyTiersUpdate) {
    return this.http.post(ADMIN_API_URL.weeklyTargets, payload)
  }

  overrideWeeklyTires() {
    return this.http.post(ADMIN_API_URL.overrideWeeklyTargets, {})
  }

  overrideMonthlyTargets(): Observable<void> {
    return this.http.post(ADMIN_API_URL.overrideMonthlyTargets, {})
  }

  getProtractorLocationsList(): Observable<IAdminProtractorLocationsResponse> {
    return this.http.get(ADMIN_API_URL.protractorLocationsList)
  }

  getUsersList(info: INew_TableAction, top?: number): Observable<IAdminUsersListResponse> {
    const payload = this.tableActionsService.createRequestPayload(info)
    return this.http.post(ADMIN_API_URL.usersList, { ...payload, ...(top ? { top, skip: 0 } : {}) })
  }

  getUserPermissions(userId: string): Observable<IAdminUserPermissionsResponse> {
    return this.http.get(ADMIN_API_URL.userPermissions(userId))
  }

  updateUserPermissions(userId: string, payload: IAdminUpdatePermissionsRequest) {
    return this.http.post(ADMIN_API_URL.userPermissions(userId), payload)
  }

  getAllPermissions(): Observable<Permission[]> {
    return this.http.get(ADMIN_API_URL.userPermissions())
  }

  bulkEditLocations(payload: IBulkEditLocationsRequest) {
    return this.http.post(ADMIN_API_URL.bulkEditLocations, payload)
  }

  bulkEditPermissions(payload: IBulkEditPermissionsRequest) {
    return this.http.post(ADMIN_API_URL.bulkEditPermissions, payload)
  }

  bulkInsertPermissions(payload: IBulkEditPermissionsRequest) {
    return this.http.post(ADMIN_API_URL.bulkInsertPermissions, payload)
  }

  getAdminLocationsList(info: INew_TableAction): Observable<IAdminLocationsListResponse> {
    return this.http.post(
      ADMIN_API_URL.adminLocationsList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  exportAdminLocationsList(info: INew_TableAction): Observable<IExportFileResponse> {
    return this.http.post(
      ADMIN_API_URL.exportAdminLocationsList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  adminGetLocation(locationId: string): Observable<IAdminLocationInfo> {
    return this.http.get(ADMIN_API_URL.adminLocationBase(locationId))
  }

  adminUpdateLocation(locationId: string, payload: IAdminUpdateLocationRequest) {
    return this.http.post(ADMIN_API_URL.adminLocationBase(locationId), payload)
  }

  adminCreateLocation(payload: IAdminCreateLocationRequest) {
    return this.http.post(ADMIN_API_URL.createLocation, payload)
  }

  adminGetDailyReports(locationId: string): Observable<IAdminDailyReports> {
    return this.http.get(ADMIN_API_URL.dailyReports(locationId))
  }

  adminImportDailyReports(locationId: string, payload: IAdminDailyReports) {
    return this.http.post(ADMIN_API_URL.dailyReports(locationId), {
      ...payload,
      startDate: this.dateService.momentToDateString(payload.startDate),
      endDate: this.dateService.momentToDateString(payload.endDate)
    })
  }

  adminImportPreLLAInvoices(locationId: string, payload: IAdminDailyReports) {
    return this.http.post(ADMIN_API_URL.preLlaInvoices(locationId), {
      ...payload,
      startDate: this.dateService.momentToDateString(payload.startDate),
      endDate: this.dateService.momentToDateString(payload.endDate)
    })
  }

  postQueueMessage(payload: IPostQueueMessageRequest) {
    return this.http.post(ADMIN_API_URL.queueMessages, payload)
  }

  adminGetAttentionProposals(): Observable<IAttentionProposalsResponse> {
    return this.http.get(ADMIN_API_URL.attentionProposals)
  }

  adminApplyAttentionProposals(payload: IApplyAttentionProposalsRequest) {
    return this.http.post(ADMIN_API_URL.applyAttentionProposals, payload)
  }

  adminCheckUsafAccount(accountNumber: string): Observable<IAdminUsafAccountStatus> {
    return this.http.get(ADMIN_API_URL.usafAccountStatus, { params: { accountNumber } })
  }
}
